/**
 * Fachkraft-Anteil für Ausbildungs-Stellen.
 *
 * Einzige Quelle der Wahrheit für:
 * - die Anzeige des Anteils im PersonalkostenTab
 * - die Berechnung des „Vorhandenen Fachpersonals" im Belegung-Tab
 *
 * Mit Migration 0017 wird der Anteil aus den Tarif-Stammdaten der Stelle
 * abgeleitet — es gibt keinen Free-Text-Enum „Ausbildungsform" mehr.
 * Domain-Regeln spiegeln den TVöD-SuE Anlage E (Stand 2026):
 *
 *  - I-SozPäd / II-Erz / III-KiPfl (Anerkennungsjahr) → 50 % unabh. von Stufe
 *  - IV-PivA, nicht landesgefördert (berufsbegleitend) → 100 % über alle Stufen
 *  - IV-PivA, landesgefördert → Stufe 1 = 0 %, 2 = 30 %, 3 = 70 %
 */

export const EG_ANERKENNUNGSJAHR: ReadonlyArray<string> = [
  'I-SozPäd',
  'II-Erz',
  'III-KiPfl',
];

export const EG_PIVA = 'IV-PivA';

/** EG-Optionen für Ausbildungs-Stellen (Anlage E TVöD-SuE). */
export const AUSBILDUNG_EG_OPTIONS: ReadonlyArray<string> = [
  ...EG_ANERKENNUNGSJAHR,
  EG_PIVA,
];

const PIVA_GEFOERDERT_PRO_STUFE: Record<string, number> = {
  '1': 0,
  '2': 0.3,
  '3': 0.7,
};

export function isAnerkennungsjahrEg(eg: string | null | undefined): boolean {
  return eg != null && EG_ANERKENNUNGSJAHR.includes(eg);
}

export function isPivaEg(eg: string | null | undefined): boolean {
  return eg === EG_PIVA;
}

/**
 * Fachkraft-Anteil (Faktor 0..1) für eine Ausbildungs-Stelle.
 * Unbekannte EG-Werte ergeben 0 — defensive Default-Logik.
 */
export function ausbildungAnteil(
  eg: string | null | undefined,
  stufe: string | null | undefined,
  pivaLandesgefoerdert: boolean | null | undefined,
): number {
  if (eg == null) return 0;
  if (isAnerkennungsjahrEg(eg)) return 0.5;
  if (isPivaEg(eg)) {
    if (!pivaLandesgefoerdert) return 1; // false oder null
    return PIVA_GEFOERDERT_PRO_STUFE[String(stufe ?? '')] ?? 0;
  }
  return 0;
}

/** Human-readable Label für die Anteil-Anzeige in Tabellen / Badges. */
export function ausbildungLabel(
  eg: string | null | undefined,
  stufe: string | null | undefined,
  pivaLandesgefoerdert: boolean | null | undefined,
): string {
  if (eg == null) return '—';
  if (isAnerkennungsjahrEg(eg)) return `${eg} (Anerkennungsjahr)`;
  if (isPivaEg(eg)) {
    const stufeLabel = stufe ? `Stufe ${stufe}` : '—';
    const foerderLabel = pivaLandesgefoerdert
      ? 'landesgefördert'
      : 'nicht gefördert';
    return `${eg}, ${stufeLabel}, ${foerderLabel}`;
  }
  return eg;
}

/**
 * Höchstes Ausbildungsjahr, für das es Sätze gibt.
 *
 * Anlage E kennt drei Stufen, die Landesförderung drei Jahressätze. Eine
 * Ausbildung in Teilzeit kann länger dauern; dann bleibt es beim dritten.
 * Spiegelt ``lft_calc/ausbildung.py``.
 */
export const MAX_AUSBILDUNGSJAHR = 3;

/** Monatsgenaues Datum aus einem ISO-String (``2026-08-01``). */
function monatsDatum(iso: string | null | undefined): { jahr: number; monat: number } | null {
  if (!iso) return null;
  const [jahr, monat] = iso.split('-').map(Number);
  if (!Number.isFinite(jahr) || !Number.isFinite(monat)) return null;
  return { jahr, monat };
}

/**
 * Im wievielten Ausbildungsjahr steht die Ausbildung in diesem Monat?
 *
 * Ausbildungen beginnen im August oder September, geplant wird nach
 * Kalenderjahren — das Ausbildungsjahr wechselt deshalb mitten im
 * Förderjahr. Gekappt auf 1..{@link MAX_AUSBILDUNGSJAHR}.
 */
export function ausbildungsjahrImMonat(
  start: string | null | undefined,
  foerderjahr: number,
  monat: number,
): number {
  const s = monatsDatum(start);
  if (s == null) return 1;
  const monateSeitStart = (foerderjahr - s.jahr) * 12 + (monat - s.monat);
  const jahr = Math.floor(monateSeitStart / 12) + 1;
  return Math.max(1, Math.min(MAX_AUSBILDUNGSJAHR, jahr));
}

/** Läuft die Ausbildung in diesem Monat des Förderjahres? */
export function laeuftImMonat(
  start: string | null | undefined,
  ende: string | null | undefined,
  foerderjahr: number,
  monat: number,
): boolean {
  const s = monatsDatum(start);
  const e = monatsDatum(ende);
  if (s && (foerderjahr < s.jahr || (foerderjahr === s.jahr && monat < s.monat))) {
    return false;
  }
  if (e && (foerderjahr > e.jahr || (foerderjahr === e.jahr && monat > e.monat))) {
    return false;
  }
  return true;
}

/** Hat die Stelle überhaupt einen Ausbildungszeitraum hinterlegt? */
export function hatAusbildungszeitraum(p: {
  ausbildung_start: string | null;
  ausbildung_ende: string | null;
}): boolean {
  return p.ausbildung_start != null || p.ausbildung_ende != null;
}

/**
 * Der Verlauf einer Ausbildung über ein Förderjahr.
 *
 * Liefert je Monat das Ausbildungsjahr und den Fachkraft-Anteil — oder
 * ``null`` für Monate außerhalb des Zeitraums. Grundlage für die
 * Anteil-Anzeige und den Fachkräftestand, die beide mit dem
 * Jahresmittel rechnen müssen und nicht mit einem Stichtagswert.
 */
export function ausbildungVerlauf(
  p: {
    eg_gruppe: string | null;
    eg_stufe: string | null;
    piva_landesgefoerdert: boolean | null;
    ausbildung_start: string | null;
    ausbildung_ende: string | null;
  },
  foerderjahr: number,
): ReadonlyArray<{ monat: number; ausbildungsjahr: number; anteil: number } | null> {
  return Array.from({ length: 12 }, (_, i) => {
    const monat = i + 1;
    if (!laeuftImMonat(p.ausbildung_start, p.ausbildung_ende, foerderjahr, monat)) {
      return null;
    }
    const jahr = p.ausbildung_start
      ? ausbildungsjahrImMonat(p.ausbildung_start, foerderjahr, monat)
      : Number(p.eg_stufe ?? 1);
    return {
      monat,
      ausbildungsjahr: jahr,
      anteil: ausbildungAnteil(p.eg_gruppe, String(jahr), p.piva_landesgefoerdert),
    };
  });
}

/**
 * Fachkraft-Anteil im Jahresmittel, gewichtet mit den aktiven Monaten.
 *
 * Bei landesgeförderter PivA springt der Anteil mit dem Ausbildungsjahr
 * (0 / 30 / 70 %). Ein Jahr, das zur Hälfte im ersten und zur Hälfte im
 * zweiten Ausbildungsjahr liegt, zählt entsprechend anteilig.
 */
export function ausbildungAnteilJahresmittel(
  p: {
    eg_gruppe: string | null;
    eg_stufe: string | null;
    piva_landesgefoerdert: boolean | null;
    ausbildung_start: string | null;
    ausbildung_ende: string | null;
  },
  foerderjahr: number,
): number {
  const verlauf = ausbildungVerlauf(p, foerderjahr);
  const summe = verlauf.reduce((acc, m) => acc + (m?.anteil ?? 0), 0);
  return summe / 12;
}

/**
 * Die Ausbildungsjahre, die in dieses Förderjahr fallen — in der Reihenfolge
 * ihres Auftretens, ohne Wiederholung.
 *
 * Meist zwei: Eine im August begonnene Ausbildung ist im Folgejahr bis Juli
 * im einen und ab August im nächsten Ausbildungsjahr.
 */
export function ausbildungsjahreImFoerderjahr(
  p: {
    eg_stufe: string | null;
    ausbildung_start: string | null;
    ausbildung_ende: string | null;
  },
  foerderjahr: number,
): number[] {
  const out: number[] = [];
  for (let monat = 1; monat <= 12; monat += 1) {
    if (!laeuftImMonat(p.ausbildung_start, p.ausbildung_ende, foerderjahr, monat)) {
      continue;
    }
    const jahr = p.ausbildung_start
      ? ausbildungsjahrImMonat(p.ausbildung_start, foerderjahr, monat)
      : Number(p.eg_stufe ?? 1);
    if (out[out.length - 1] !== jahr) out.push(jahr);
  }
  return out;
}

/**
 * Erster und letzter Monat, in dem die Ausbildung im Förderjahr läuft.
 *
 * ``null``, wenn sie dieses Jahr gar nicht läuft — dann kostet die Stelle
 * nichts und wird auch nicht gefördert.
 */
export function aktiveMonateImFoerderjahr(
  p: { ausbildung_start: string | null; ausbildung_ende: string | null },
  foerderjahr: number,
): { ab: number; bis: number } | null {
  const monate = [];
  for (let monat = 1; monat <= 12; monat += 1) {
    if (laeuftImMonat(p.ausbildung_start, p.ausbildung_ende, foerderjahr, monat)) {
      monate.push(monat);
    }
  }
  if (monate.length === 0) return null;
  return { ab: monate[0], bis: monate[monate.length - 1] };
}
